// The one place the web UI touches ocs-proto directly: web/packages/ocs-proto is the
// wasm-pack output of crates/ocs-proto (npm run build:proto). Everything cryptographic
// and every relay frame goes through it (docs/09 principle 4); this module only loads
// the module once and gives its `any`-typed exports the frame types from ./frames.
import init, * as wasm from "ocs-proto";

import { type ClientFrame, FRAME_TYPES, type SigAlg } from "./frames";
import type { RecipientList, SignedRecipientList, VerifyingKey } from "./recipients";

export type { InitInput } from "ocs-proto";

export class FrameDecodeError extends Error {
  constructor(reason: string) {
    super(`relay frame: ${reason}`);
    this.name = "FrameDecodeError";
  }
}

/** ocs-proto's browser surface, typed. Byte arguments and results are `Uint8Array`. */
export interface Proto {
  /** `ocs_proto::PROTO_VERSION`. */
  readonly version: number;
  encodeFrame(frame: ClientFrame): Uint8Array;
  /** Throws FrameDecodeError on anything that is not a client-edge frame. */
  decodeFrame(bytes: Uint8Array): ClientFrame;
  eventAad(headerCbor: Uint8Array): Uint8Array;
  decryptEvent(cek: Uint8Array, eventCbor: Uint8Array): Uint8Array;
  encryptInboundEvent(cek: Uint8Array, nonce: Uint8Array, headerCbor: Uint8Array, plaintext: Uint8Array): Uint8Array;
  wrappedCekEnc(wrapped: Uint8Array): Uint8Array;
  unwrapCekWithSharedSecret(
    dh: Uint8Array,
    recipientPublicKey: Uint8Array,
    wrapped: Uint8Array,
    conversationId: Uint8Array,
    segmentId: Uint8Array,
    deviceId: Uint8Array,
  ): Uint8Array;
  /** The CBOR document of a recipient list, exactly as the account key signs it. */
  recipientListDocument(list: RecipientList): Uint8Array;
  /** The bytes WebCrypto signs for a document at `signedAt` (ms since the epoch). */
  recipientListSigningInput(signedAt: number, document: Uint8Array): Uint8Array;
  /** Verify a signed list against the account key (`SignedRecipientList::verify`); throws if it does not. */
  verifyRecipientList(accountKey: VerifyingKey, signed: SignedRecipientList): RecipientList;
  /** The account key's 64-character lowercase hex fingerprint (D41). */
  accountKeyFingerprint(alg: SigAlg, publicKey: Uint8Array): string;
  /**
   * Seal a credential value to the daemon's sealing key, bound to `orgId` and `kind`
   * (`ocs_proto::secret`). `ikm` is 32 fresh random bytes; the result is the envelope.
   */
  sealCredentialValue(sealingPublicKey: Uint8Array, orgId: string, kind: string, ikm: Uint8Array, value: Uint8Array): Uint8Array;
  /**
   * Seal a managed account key's private half (the 32-byte scalar) to the daemon's sealing
   * key, bound to `userId` and `alg` (D59, `ocs_proto::managed`).
   */
  sealAccountKey(sealingPublicKey: Uint8Array, userId: string, alg: SigAlg, ikm: Uint8Array, privateKey: Uint8Array): Uint8Array;
  /** The 16-hex-character id of a sealing public key, as `GET /api/sealing-key` names it. */
  sealingKeyId(sealingPublicKey: Uint8Array): string;
  /** The bytes a device signs for a signed request (`ocs_proto::request`), domain included. */
  signedRequestSigningInput(method: string, path: string, timestampMs: number, payload: Uint8Array): Uint8Array;
}

function decodeFrame(bytes: Uint8Array): ClientFrame {
  let frame: unknown;
  try {
    frame = wasm.decodeFrame(bytes);
  } catch (e) {
    throw new FrameDecodeError(e instanceof Error ? e.message : String(e));
  }
  // serde already validated the shape against ClientFrame; the type name is the one
  // fact worth re-checking before the cast.
  if (!frame || typeof frame !== "object" || !FRAME_TYPES.has((frame as { type?: unknown }).type as string)) {
    throw new FrameDecodeError("unknown frame type");
  }
  return frame as ClientFrame;
}

const api: Omit<Proto, "version"> = {
  encodeFrame: (frame) => wasm.encodeFrame(frame),
  decodeFrame,
  eventAad: wasm.eventAad,
  decryptEvent: wasm.decryptEvent,
  encryptInboundEvent: wasm.encryptInboundEvent,
  wrappedCekEnc: wasm.wrappedCekEnc,
  unwrapCekWithSharedSecret: wasm.unwrapCekWithSharedSecret,
  recipientListDocument: (list) => wasm.recipientListDocument(list),
  recipientListSigningInput: (signedAt, document) => wasm.recipientListSigningInput(BigInt(signedAt), document),
  verifyRecipientList: (key, signed) =>
    wasm.verifyRecipientList(
      key.alg,
      key.bytes,
      signed.document,
      BigInt(signed.signedAt),
      signed.signature.alg,
      signed.signature.bytes,
    ) as RecipientList,
  accountKeyFingerprint: (alg, publicKey) => wasm.accountKeyFingerprint(alg, publicKey),
  sealCredentialValue: wasm.sealCredentialValue,
  sealAccountKey: wasm.sealAccountKey,
  sealingKeyId: wasm.sealingKeyId,
  signedRequestSigningInput: (method, path, timestampMs, payload) =>
    wasm.signedRequestSigningInput(method, path, BigInt(timestampMs), payload),
};

let loading: Promise<Proto> | undefined;

/**
 * Initialise the wasm module once and return the typed surface. In the browser the
 * default locates `ocs_proto_bg.wasm` next to the glue (Vite serves it as an asset);
 * Node callers (tests, Playwright fixtures) pass the bytes or a compiled module.
 */
export function loadProto(source?: wasm.InitInput): Promise<Proto> {
  loading ??= init(source === undefined ? undefined : { module_or_path: source }).then(() => ({
    version: wasm.version(),
    ...api,
  }));
  return loading;
}
